/**
 * Presentation normalization (spec-13 §4).
 *
 * The frontend prefers the server-provided `presentation` object. For
 * responses that predate the contract, `presentationForException` rebuilds an
 * equivalent one client-side from the legacy DTO fields so no surface depends
 * on raw English. Parameter projection always applies the per-code allowlist —
 * arbitrary `detected_payload` fields are never forwarded into messages.
 */

import type { ExceptionReportDto } from '@/features/mass-balance/types';

import { issueCatalogEntry } from './catalog';
import type { IssuePresentation, IssueSeverity, IssueTarget } from './types';

const SEVERITIES: readonly IssueSeverity[] = ['info', 'warning', 'error', 'critical'];

const asSeverity = (value: string | undefined, fallback: IssueSeverity): IssueSeverity =>
  SEVERITIES.includes(value as IssueSeverity) ? (value as IssueSeverity) : fallback;

/** Project raw params through a per-code allowlist; drop everything else. */
export function projectMessageParams(
  code: string,
  raw: Record<string, unknown> | null | undefined,
  opts?: { supplier?: boolean },
): Record<string, unknown> {
  const entry = issueCatalogEntry(code);
  if (!entry || !raw) return {};
  const allowed = opts?.supplier ? (entry.supplierParams ?? []) : entry.allowedParams;
  const out: Record<string, unknown> = {};
  for (const key of allowed) {
    const value = raw[key];
    if (value === null || value === undefined) continue;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Normalize one exception row: prefer the server presentation, else rebuild
 * an equivalent object client-side.
 */
export function presentationForException(
  dto: ExceptionReportDto & { presentation?: IssuePresentation | null },
): IssuePresentation {
  if (dto.presentation) return dto.presentation;

  // Round-3 item 3: a completed spec-13 endpoint always supplies
  // `presentation`; when it is absent the client CANNOT verify the target's
  // existence, type, batch, or tenancy, so the fallback fails closed. It
  // never rebuilds a deep target from raw legacy UUIDs and never claims
  // `can_open_target` — at most it offers the parent-batch intent, which the
  // link component re-checks against the viewer's own route access.
  const entry = issueCatalogEntry(dto.exception_code);
  // Origin is still useful for localized copy, but legacy records never drive
  // deep links because the client cannot reauthorize raw target ids.
  const origin: IssuePresentation['message_origin'] = dto.detected_payload ? 'system' : 'manual';
  const payload = (dto.detected_payload ?? {}) as Record<string, unknown>;
  const target: IssueTarget = {
    monthly_batch_id: dto.monthly_batch_id,
    batch_display: null,
    record_type: 'monthly_batch',
    record_id: null,
    record_code: null,
    section_key: entry?.sectionKey ?? null,
    field_key: null,
    navigation: { kind: 'monthly_batch', params: { batch_id: dto.monthly_batch_id } },
  };
  return {
    issue_code: dto.exception_code,
    message_origin: origin,
    message_key: entry ? `efs.issues.codes.${dto.exception_code}.message` : null,
    message_params: entry
      ? projectMessageParams(dto.exception_code, {
          ...payload,
          name:
            payload.name ??
            payload.japanese_name ??
            payload.material_name ??
            payload.destination_name,
        })
      : {},
    target,
    next_action_key: entry ? `efs.issues.codes.${dto.exception_code}.action` : null,
    next_action_params: {},
    severity: asSeverity(dto.severity, entry?.defaultSeverity ?? 'warning'),
    can_open_target: false,
    detected_at: dto.created_at ?? null,
    legacy_description: dto.description ?? null,
  };
}
